import {
  FeatureFlagService,
  FeatureFlag,
  FeatureFlagKey,
  EnvironmentType,
} from "@/types/feature-flags";
import {
  getCurrentFeatureFlagConfig,
  getFeatureFlagConfigWithAccountOverrides,
} from "./config";

/**
 * Singleton Feature Flag Manager
 * Manages feature flags without React state dependencies to prevent memory leaks
 */
class FeatureFlagManager {
  private static instance: FeatureFlagManager;
  private service: FeatureFlagService;
  private flags: Partial<Record<FeatureFlagKey, FeatureFlag>> = {};
  private isInitialized = false;
  private subscribers: Set<() => void> = new Set();
  private currentAccountId?: number;

  private constructor(service?: FeatureFlagService) {
    this.service = service || this.createDefaultService();
  }

  public static getInstance(service?: FeatureFlagService): FeatureFlagManager {
    if (!FeatureFlagManager.instance) {
      FeatureFlagManager.instance = new FeatureFlagManager(service);
    }
    return FeatureFlagManager.instance;
  }

  private createDefaultService(): FeatureFlagService {
    // Import here to avoid circular dependencies
    // We'll create a simple service directly to avoid require issues
    return new (class implements FeatureFlagService {
      private flags: Partial<Record<FeatureFlagKey, FeatureFlag>> = {};
      private config = getCurrentFeatureFlagConfig();

      async getFlags(): Promise<Partial<Record<FeatureFlagKey, FeatureFlag>>> {
        return { ...this.config.flags };
      }

      async refreshFlags(): Promise<void> {
        // No-op for local service
      }

      isEnabled(flagKey: FeatureFlagKey): boolean {
        const flag = this.config.flags[flagKey];
        if (!flag) return false;
        if (!flag.enabled) return false;
        if (
          flag.environments &&
          !flag.environments.includes(this.config.environment)
        )
          return false;
        return true;
      }

      getFlag(flagKey: FeatureFlagKey): FeatureFlag | undefined {
        return this.config.flags[flagKey];
      }
    })();
  }

  /**
   * Initialize the manager with flags
   */
  public async initialize(accountId?: number): Promise<void> {
    this.currentAccountId = accountId;

    if (this.isInitialized) {
      return;
    }

    try {
      this.flags = await this.service.getFlags();
      this.isInitialized = true;
      this.notifySubscribers();
    } catch (error) {
      console.error("Failed to initialize feature flags:", error);
      // Initialize with empty flags to prevent blocking
      this.flags = {};
      this.isInitialized = true;
      this.notifySubscribers();
    }
  }

  /**
   * Set the current account ID for account-specific overrides
   */
  public setAccountId(accountId: number): void {
    this.currentAccountId = accountId;
    this.refreshFlags();
  }

  /**
   * Clear the current account ID
   */
  public clearAccountId(): void {
    this.currentAccountId = undefined;
    this.refreshFlags();
  }

  /**
   * Check if a feature flag is enabled
   */
  public isEnabled(flagKey: FeatureFlagKey): boolean {
    // Ensure we're initialized
    if (!this.isInitialized) {
      this.initialize();
      return false; // Return false while initializing
    }

    // Get configuration with account overrides
    const environment = this.getCurrentEnvironment();
    const config = getFeatureFlagConfigWithAccountOverrides(
      environment,
      this.currentAccountId
    );
    const flag = config.flags[flagKey];

    if (!flag) {
      return false;
    }

    return flag.enabled;
  }

  /**
   * Get a feature flag by key
   */
  public getFlag(flagKey: FeatureFlagKey): FeatureFlag | undefined {
    if (!this.isInitialized) {
      this.initialize();
      return undefined;
    }

    const environment = this.getCurrentEnvironment();
    const config = getFeatureFlagConfigWithAccountOverrides(
      environment,
      this.currentAccountId
    );
    return config.flags[flagKey];
  }

  /**
   * Get all feature flags
   */
  public getAllFlags(): Partial<Record<FeatureFlagKey, FeatureFlag>> {
    if (!this.isInitialized) {
      this.initialize();
      return {};
    }

    const environment = this.getCurrentEnvironment();
    const config = getFeatureFlagConfigWithAccountOverrides(
      environment,
      this.currentAccountId
    );
    return { ...config.flags };
  }

  /**
   * Refresh flags from the service
   */
  public async refreshFlags(): Promise<void> {
    try {
      await this.service.refreshFlags();
      this.flags = await this.service.getFlags();
      this.notifySubscribers();
    } catch (error) {
      console.error("Failed to refresh feature flags:", error);
    }
  }

  /**
   * Update override flags
   */
  public updateOverrideFlags(
    overrideFlags: Partial<Record<FeatureFlagKey, FeatureFlag>>
  ): void {
    if (this.service && "updateOverrideFlags" in this.service) {
      (this.service as any).updateOverrideFlags(overrideFlags);
      this.refreshFlags();
    }
  }

  /**
   * Subscribe to flag changes
   */
  public subscribe(callback: () => void): () => void {
    this.subscribers.add(callback);

    // Return unsubscribe function
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Notify all subscribers of changes
   */
  private notifySubscribers(): void {
    this.subscribers.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error("Error in feature flag subscriber:", error);
      }
    });
  }

  /**
   * Get current environment
   */
  public getCurrentEnvironment(): EnvironmentType {
    return getCurrentFeatureFlagConfig().environment;
  }

  /**
   * Get current account ID
   */
  public getCurrentAccountId(): number | undefined {
    return this.currentAccountId;
  }

  /**
   * Check if manager is initialized
   */
  public getIsInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Reset the singleton (useful for testing)
   */
  public static reset(): void {
    if (FeatureFlagManager.instance) {
      FeatureFlagManager.instance.subscribers.clear();
      FeatureFlagManager.instance = null as any;
    }
  }
}

// Export singleton instance
export const featureFlagManager = FeatureFlagManager.getInstance();

// Export the class for testing
export { FeatureFlagManager };
